// # Review Statistics Component for Step 1
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../../auth/state';
import { doc, onSnapshot, getDoc } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { FaChevronDown, FaChevronRight, FaStar, FaCalendarAlt, FaClock, FaRegCheckCircle, FaHistory } from 'react-icons/fa';
import './stats1_review.css';
import { getBasePath } from '../m1_access';
import PropTypes from 'prop-types';

// # Import chapter icons
import anatomyIcon from '../../../general/library/step1/step1_icons/anatomy.png';
import biochemistryIcon from '../../../general/library/step1/step1_icons/biochemistry.png';
import bloodIcon from '../../../general/library/step1/step1_icons/blood.png';
import cnsIcon from '../../../general/library/step1/step1_icons/cns.png';
import endocrineIcon from '../../../general/library/step1/step1_icons/endocrine.png';
import eyeIcon from '../../../general/library/step1/step1_icons/eye.png';
import gitIcon from '../../../general/library/step1/step1_icons/git.png';
import heartIcon from '../../../general/library/step1/step1_icons/heart.png';
import immunologyIcon from '../../../general/library/step1/step1_icons/immunology.png';
import infectiousIcon from '../../../general/library/step1/step1_icons/infectious.png';
import kidneyIcon from '../../../general/library/step1/step1_icons/kidney.png';
import lungsIcon from '../../../general/library/step1/step1_icons/lungs.png';
import musculoskeletalIcon from '../../../general/library/step1/step1_icons/musculoskeletal.png';
import pathologyIcon from '../../../general/library/step1/step1_icons/pathology.png';
import pharmaIcon from '../../../general/library/step1/step1_icons/pharma.png';
import psychiatryIcon from '../../../general/library/step1/step1_icons/psychiatry.png';
import publicHealthIcon from '../../../general/library/step1/step1_icons/public_health.png';
import reproductiveIcon from '../../../general/library/step1/step1_icons/reproductive-system.png';
import revisionIcon from '../../../general/library/step1/step1_icons/revision.png';
import skinIcon from '../../../general/library/step1/step1_icons/skin.png';

// # Import chapter topics mapping
import { chapterTopics } from '../../../general/library/step1/step1_chapter.js';
import { chapters } from '../../../general/library/step1/library_step_1.js';

// # Chapter icons mapping
const chapterIcons = {
  biochemistry: biochemistryIcon,
  immunology: immunologyIcon,
  microbiology: infectiousIcon,
  general_pathology: pathologyIcon,
  pharmacology: pharmaIcon,
  biostatistics: publicHealthIcon,
  cardiovascular: heartIcon,
  endocrine: endocrineIcon,
  gastrointestinal: gitIcon,
  hematology_and_oncology: bloodIcon,
  musculoskeletal_and_connective_tissue: musculoskeletalIcon,
  dermatology: skinIcon,
  neurology: cnsIcon,
  special_senses: eyeIcon,
  psychiatry: psychiatryIcon,
  renal: kidneyIcon,
  reproductive: reproductiveIcon,
  respiratory: lungsIcon,
  rapid_review: revisionIcon
};

// # Helper function to format date
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

// # Helper function to calculate days ago
const getDaysAgo = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  const now = new Date();
  const diffTime = Math.abs(now - date);
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  return diffDays === 0 ? 'Today' : diffDays === 1 ? 'Yesterday' : `${diffDays} days ago`;
};

function ReviewStats({ isAdmin, isTutor, isMentorView, isDarkMode, targetUserId }) {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [expandedChapters, setExpandedChapters] = useState({});
  const [libraryData, setLibraryData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reviewStats, setReviewStats] = useState({
    totalReviews: 0,
    totalReviewedTopics: 0,
    recentReviews: [],
    mostReviewedTopics: [],
    reviewsByChapter: {},
    reviewsByDay: {},
    reviewTimeline: []
  });
  const [activeTab, setActiveTab] = useState('recent');

  // # Load library data and process review statistics
  useEffect(() => {
    // # Get the correct user ID based on roles
    const userId = targetUserId || user?.uid;
    if (!userId) return;

    setLoading(true);
    const unsubscribe = onSnapshot(doc(db, 'my_lib_progress', userId), (docSnapshot) => {
      if (docSnapshot.exists()) {
        const data = docSnapshot.data();
        if (data.step1) {
          setLibraryData(data.step1);
          processReviewData(data.step1);
          setLoading(false);
        } else {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, [user, targetUserId]);

  // # Process library data to extract review statistics
  const processReviewData = (data) => {
    let totalReviews = 0;
    let totalReviewedTopics = 0;
    const allReviews = [];
    const reviewsByChapter = {};
    const reviewsByDay = {};
    const topicReviewCounts = [];

    // # Process each chapter
    Object.entries(data).forEach(([chapterSlug, chapterData]) => {
      if (typeof chapterData === 'object' && chapterData !== null) {
        let chapterReviews = 0;
        let chapterTopicsReviewed = 0;
        const chapterTopicsList = [];
        const totalChapterTopics = chapterTopics[chapterSlug]?.length || 0;
        
        // # Process each topic in the chapter
        Object.entries(chapterData).forEach(([topicId, topicData]) => {
          if (topicId !== 'iscomplete' && topicId !== 'iscomplete_at') {
            const hasReviews = topicData?.reviewTimestamps?.length > 0;
            const reviewCount = hasReviews ? topicData.reviewTimestamps.length : 0;
            
            // # Get topic name
            const topicName = chapterTopics[chapterSlug]?.find(t => t.id === topicId)?.name || topicId;
            
            // # Add to chapter topics list
            chapterTopicsList.push({
              topicId,
              topicName,
              reviewCount,
              lastReview: hasReviews ? 
                Math.max(...topicData.reviewTimestamps.map(ts => new Date(ts).getTime())) : null,
              difficulty: topicData.difficulty || 'unknown',
              isCompleted: topicData.completed || false
            });

            if (hasReviews) {
              totalReviews += reviewCount;
              chapterReviews += reviewCount;
              chapterTopicsReviewed++;
              totalReviewedTopics++;
              
              // # Format chapter name
              const chapterName = chapterSlug
                .split('_')
                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ');
              
              // # Add to most reviewed topics list
              topicReviewCounts.push({
                chapter: chapterSlug,
                chapterName,
                topicId,
                topicName,
                reviewCount,
                lastReview: Math.max(...topicData.reviewTimestamps.map(ts => new Date(ts).getTime()))
              });
              
              // # Process each review timestamp
              topicData.reviewTimestamps.forEach(timestamp => {
                const reviewDate = new Date(timestamp);
                const dateKey = reviewDate.toISOString().split('T')[0];
                
                // # Add to reviews by day
                if (!reviewsByDay[dateKey]) {
                  reviewsByDay[dateKey] = 0;
                }
                reviewsByDay[dateKey]++;
                
                // # Add to all reviews list
                allReviews.push({
                  timestamp,
                  chapter: chapterSlug,
                  chapterName,
                  topicId,
                  topicName,
                  difficulty: topicData.difficulty || 'unknown'
                });
              });
            }
          }
        });
        
        // # Add chapter review data
        if (totalChapterTopics > 0) {
          reviewsByChapter[chapterSlug] = {
            name: chapterSlug
              .split('_')
              .map(word => word.charAt(0).toUpperCase() + word.slice(1))
              .join(' '),
            totalReviews: chapterReviews,
            topicsReviewed: chapterTopicsReviewed,
            totalTopics: totalChapterTopics,
            percentage: Math.round((chapterTopicsReviewed / totalChapterTopics) * 100),
            topics: chapterTopicsList.sort((a, b) => b.reviewCount - a.reviewCount)
          };
        }
      }
    });
    
    // # Sort all reviews by date (newest first)
    allReviews.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    
    // # Sort most reviewed topics
    topicReviewCounts.sort((a, b) => b.reviewCount - a.reviewCount);
    
    // # Update state with processed data
    setReviewStats({
      totalReviews,
      totalReviewedTopics,
      recentReviews: allReviews.slice(0, 50), // Get most recent 50 reviews
      mostReviewedTopics: topicReviewCounts.slice(0, 20), // Get top 20 most reviewed topics
      reviewsByChapter,
      reviewsByDay
    });
  };

  // # Toggle chapter expansion
  const toggleChapterExpansion = (chapterSlug) => {
    setExpandedChapters(prev => ({
      ...prev,
      [chapterSlug]: !prev[chapterSlug]
    }));
  };

  // # Handle topic navigation
  const handleTopicClick = (chapter, topicId) => {
    const isAdmin = window.location.pathname.startsWith('/admin');
    let basePath;
    
    if (isAdmin) {
      basePath = '/admin/library/step1';
    } else if (isTutor) {
      basePath = '/tutor-dashboard/library/step1';
    } else {
      basePath = '/library/step1';
    }
    
    navigate(`${basePath}/${chapter}/topic/${topicId}`);
  };

  // # Group reviews by date for display
  const groupReviewsByDate = (reviews) => {
    const grouped = {};
    reviews.forEach(review => {
      const date = new Date(review.timestamp).toISOString().split('T')[0];
      if (!grouped[date]) {
        grouped[date] = [];
      }
      grouped[date].push(review);
    });
    return grouped;
  };

  // # Get the difficulty color class
  const getDifficultyColorClass = (difficulty) => {
    switch (difficulty) {
      case 'easy': return 'stats1_rev_easy';
      case 'good': return 'stats1_rev_good';
      case 'hard': return 'stats1_rev_hard';
      case 'oh_no': return 'stats1_rev_oh_no';
      default: return '';
    }
  };

  // # Render the review stats content based on active tab
  const renderTabContent = () => {
    switch (activeTab) {
      case 'recent':
        return renderRecentReviews();
      case 'most':
        return renderMostReviewedTopics();
      case 'chapters':
        return renderChapterReviews();
      default:
        return renderRecentReviews();
    }
  };

  // # Render the recent reviews tab content
  const renderRecentReviews = () => {
    const groupedReviews = groupReviewsByDate(reviewStats.recentReviews);
    
    return (
      <div className="stats1_rev_recent">
        <div className="stats1_rev_info_cards">
          <div className="stats1_rev_info_card">
            <h3>Total Reviews</h3>
            <div className="stats1_rev_info_value">{reviewStats.totalReviews}</div>
          </div>
          <div className="stats1_rev_info_card">
            <h3>Reviewed Topics</h3>
            <div className="stats1_rev_info_value">{reviewStats.totalReviewedTopics}</div>
          </div>
        </div>
        
        {Object.entries(groupedReviews).length > 0 ? (
          Object.entries(groupedReviews).map(([date, reviews]) => (
            <div key={date} className="stats1_rev_date_group">
              <div className="stats1_rev_date_header">
                <FaCalendarAlt />
                <span>{formatDate(date)}</span>
                <span className="stats1_rev_days_ago">{getDaysAgo(date)}</span>
                <span className="stats1_rev_count">{reviews.length} reviews</span>
              </div>
              <div className="stats1_rev_items">
                {reviews.map((review, index) => (
                  <div 
                    key={`${review.topicId}_${index}`} 
                    className="stats1_rev_item"
                    onClick={() => handleTopicClick(review.chapter, review.topicId)}
                  >
                    <div className="stats1_rev_item_header">
                      <img 
                        src={chapterIcons[review.chapter]} 
                        alt={review.chapterName}
                        className="stats1_rev_chapter_icon"
                      />
                      <div className="stats1_rev_item_info">
                        <div className="stats1_rev_chapter_name">{review.chapterName}</div>
                        <div className="stats1_rev_topic_name">{review.topicName}</div>
                      </div>
                      <div className={`stats1_rev_difficulty ${getDifficultyColorClass(review.difficulty)}`}>
                        {review.difficulty?.replace('_', ' ')}
                      </div>
                    </div>
                    <div className="stats1_rev_item_time">
                      {new Date(review.timestamp).toLocaleTimeString()}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))
        ) : (
          <div className="stats1_rev_empty">
            <p>No review data available.</p>
          </div>
        )}
      </div>
    );
  };

  // # Render the most reviewed topics tab content
  const renderMostReviewedTopics = () => {
    return (
      <div className="stats1_rev_most">
        <h3>Most Frequently Reviewed Topics</h3>
        {reviewStats.mostReviewedTopics.length > 0 ? (
          <div className="stats1_rev_most_grid">
            {reviewStats.mostReviewedTopics.map((topic, index) => (
              <div 
                key={`${topic.topicId}_${index}`} 
                className="stats1_rev_most_item"
                onClick={() => handleTopicClick(topic.chapter, topic.topicId)}
              >
                <div className="stats1_rev_most_rank">{index + 1}</div>
                <div className="stats1_rev_most_content">
                  <div className="stats1_rev_most_header">
                    <img 
                      src={chapterIcons[topic.chapter]} 
                      alt={topic.chapterName}
                      className="stats1_rev_chapter_icon"
                    />
                    <div className="stats1_rev_most_info">
                      <div className="stats1_rev_chapter_name">{topic.chapterName}</div>
                      <div className="stats1_rev_topic_name">{topic.topicName}</div>
                    </div>
                  </div>
                  <div className="stats1_rev_most_stats">
                    <div className="stats1_rev_most_count">
                      <FaHistory />
                      <span>{topic.reviewCount} reviews</span>
                    </div>
                    {topic.lastReview && (
                      <div className="stats1_rev_most_last">
                        <FaClock />
                        <span>Last: {formatDate(new Date(topic.lastReview))}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="stats1_rev_empty">
            <p>No review data available.</p>
          </div>
        )}
      </div>
    );
  };

  // # Render the chapter reviews tab content
  const renderChapterReviews = () => {
    // # Sort chapters by review count
    const sortedChapters = Object.entries(reviewStats.reviewsByChapter)
      .sort(([, a], [, b]) => b.topicsReviewed - a.topicsReviewed)
      .map(([slug, data]) => ({ slug, ...data }));
    
    return (
      <div className="stats1_rev_chapters">
        <h3>Reviews by Chapter</h3>
        {sortedChapters.length > 0 ? (
          <div className="stats1_rev_chapters_list">
            {sortedChapters.map((chapter) => (
              <div key={chapter.slug} className="stats1_rev_chapter_item">
                <div 
                  className="stats1_rev_chapter_header"
                  onClick={() => toggleChapterExpansion(chapter.slug)}
                >
                  <div className="stats1_rev_chapter_header_left">
                    {expandedChapters[chapter.slug] ? <FaChevronDown /> : <FaChevronRight />}
                    <img 
                      src={chapterIcons[chapter.slug]} 
                      alt={chapter.name}
                      className="stats1_rev_chapter_icon"
                    />
                    <span className="stats1_rev_chapter_name">{chapter.name}</span>
                  </div>
                  <div className="stats1_rev_chapter_stats">
                    <span className="stats1_rev_chapter_count">
                      {chapter.topicsReviewed}/{chapter.totalTopics} topics reviewed
                    </span>
                    <div className="stats1_rev_chapter_bar">
                      <div 
                        className="stats1_rev_chapter_bar_fill"
                        style={{ width: `${chapter.percentage}%` }}
                      ></div>
                      <span className="stats1_rev_chapter_percentage">{chapter.percentage}%</span>
                    </div>
                    <span className="stats1_rev_total_reviews">
                      {chapter.totalReviews} total reviews
                    </span>
                  </div>
                </div>
                
                {expandedChapters[chapter.slug] && (
                  <div className="stats1_rev_chapter_topics">
                    <div className="stats1_rev_topics_grid">
                      {chapter.topics.map((topic) => (
                        <div 
                          key={topic.topicId}
                          className="stats1_rev_topic_card"
                          onClick={() => handleTopicClick(chapter.slug, topic.topicId)}
                        >
                          <div className="stats1_rev_topic_header">
                            <div className="stats1_rev_topic_name">{topic.topicName}</div>
                            <div className={`stats1_rev_topic_difficulty ${getDifficultyColorClass(topic.difficulty)}`}>
                              {topic.difficulty?.replace('_', ' ')}
                            </div>
                          </div>
                          <div className="stats1_rev_topic_stats">
                            <div className="stats1_rev_topic_stat">
                              <FaHistory />
                              <span>{topic.reviewCount} reviews</span>
                            </div>
                            {topic.lastReview && (
                              <div className="stats1_rev_topic_stat">
                                <FaClock />
                                <span>Last: {formatDate(new Date(topic.lastReview))}</span>
                              </div>
                            )}
                            <div className={`stats1_rev_topic_status ${topic.isCompleted ? 'completed' : ''}`}>
                              {topic.isCompleted ? 'Completed' : 'Not Completed'}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="stats1_rev_empty">
            <p>No review data available by chapter.</p>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={`stats1_rev_container ${isDarkMode ? 'stats1_rev_dark' : ''}`}>
      {loading ? (
        <div className="stats1_rev_loading">
          <div className="stats1_rev_spinner"></div>
          <p>Loading review statistics...</p>
        </div>
      ) : (
        <>
          <div className="stats1_rev_tabs">
            <button 
              className={`stats1_rev_tab ${activeTab === 'recent' ? 'active' : ''}`}
              onClick={() => setActiveTab('recent')}
            >
              <FaHistory />
              <span>Recent Reviews</span>
            </button>
            <button 
              className={`stats1_rev_tab ${activeTab === 'most' ? 'active' : ''}`}
              onClick={() => setActiveTab('most')}
            >
              <FaStar />
              <span>Most Reviewed</span>
            </button>
            <button 
              className={`stats1_rev_tab ${activeTab === 'chapters' ? 'active' : ''}`}
              onClick={() => setActiveTab('chapters')}
            >
              <FaRegCheckCircle />
              <span>By Chapter</span>
            </button>
          </div>
          
          <div className="stats1_rev_content">
            {renderTabContent()}
          </div>
        </>
      )}
    </div>
  );
}

// # PropTypes
ReviewStats.propTypes = {
  isDarkMode: PropTypes.bool,
  isAdmin: PropTypes.bool,
  isTutor: PropTypes.bool,
  isMentorView: PropTypes.bool,
  targetUserId: PropTypes.string
};

export default ReviewStats;
